/**
 * Whether the Wallet can start a top-up (MYP-134).
 *
 * The vendor's `molpay_pay` exists (guide of 2026-10-26), but there is no sandbox: its gateway
 * goes to the live Fiuu merchant, so the first real top-up is a real payment. **Off** until one
 * RM 10 top-up has gone through end to end with the mentor's OK.
 *
 * While it is off the Top up card can be filled in (amount, quick amounts, summary), but Pay does
 * not call the vendor: it says FPX top-up is not switched on yet and links to mypoz.com, where
 * top-up works today. The result page does **not** depend on it: the vendor always sends the
 * customer back to web.mypoz.com, so that page must work before this is turned on. Turning it on
 * is this one line.
 */
export const FIUU_TOPUP_ENABLED = false;

/**
 * Whether the Wallet can start an e-wallet / DuitNow QR top-up through Revenue Monster (MYP-135).
 *
 * The vendor's `rm_pay` exists (guide of 2026-11-03), but only on RM's **sandbox**: the new site
 * has no production RM merchant yet (1 URL = 1 merchant ID), and the app opens only sandbox
 * checkout pages (`RM_CHECKOUT_HOSTS`). Sandbox top-ups still credit the real wallet, so it is
 * **off** in the code. For the sandbox test it is turned on locally only, with the test account,
 * and never committed on.
 *
 * While it is off the e-wallet choice can be picked and filled in, but Pay does not call the
 * vendor: it says e-wallet top-up is not switched on yet and links to mypoz.com, where it works
 * today. Going live needs the production merchant, its checkout host in `RM_CHECKOUT_HOSTS`, and
 * the mentor's OK; then turning it on is this one line.
 */
export const RM_TOPUP_ENABLED = true;

/**
 * The e-wallet fee shown on the Top up card, in cents, or `undefined` while nobody has said what
 * it is: the old site's fee table kept saying "FPX充值 · 0" with EWALLET PAYMENT picked, so that is
 * not evidence. Unknown, the card shows a dash for the fee and for what reaches the wallet rather
 * than a guessed figure.
 */
export const RM_EWALLET_FEE_CENTS: number | undefined = undefined;

/**
 * The FPX fee shown on the Top up card, in cents. RM 0: the old mypoz.com top-up page charges
 * nothing for online banking (only cards carry a fee). Shown, not worked out, so a fee the vendor
 * later confirms is a change here alone.
 */
export const FIUU_FPX_FEE_CENTS = 0;

/**
 * Where the Top up card keeps the record id (`accdetailid`) of the top-up it last sent the
 * customer to pay, so the result page can fall back on it if the address loses it. sessionStorage:
 * per tab, and only a convenience — the result page asks `get_trade` how it stands, never this.
 */
export const LAST_TOP_UP_KEY = 'mypoz.topUp.lastAccDetailId';

/**
 * Where the Top up card keeps the payment method the customer last picked (MYP-135), so the card
 * comes back to it — after "Try again" on the result page, or Back from a payment page.
 * sessionStorage: per tab, a convenience only.
 */
export const LAST_TOP_UP_METHOD_KEY = 'mypoz.topUp.lastMethod';
